import type { FieldInputADTValue, RelativeSelectConfig } from '@lighthouse/core'
import { mergeDeepRight } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { NO_FIELD_INPUT, numberReg } from '../../../constants'
import { isFieldNumberValue, isFileValue, isIdsValue, isNumberValue, isRichTextValue, isStringArray, isTextValue } from '../../../utils'
import CalendarField from '../BaseField/CalendarField'
import { CascadeDrawerField } from '../BaseField/CascadeField/mobile'
import { CheckboxField } from '../BaseField/CheckboxField'
import { DateDrawerField } from '../BaseField/DateField/mobile'
import { DepartmentDrawerField } from '../BaseField/DepartmentField/mobile'
import EmptyField from '../BaseField/EmptyField'
import { FileDrawerField } from '../BaseField/FileField/mobile'
import { NotesModalField } from '../BaseField/NoteField/mobile'
import NumberField from '../BaseField/NumberField'
import { PersonDrawerField } from '../BaseField/PersonField/mobile'
import PhoneNumberField from '../BaseField/PhoneNumberField'
import { RelativeSelectDrawerField } from '../BaseField/RelativeSelectField/mobile'
import { SelectDrawerField } from '../BaseField/SelectField/mobile'
import SimpleTextField from '../BaseField/SimpleTextField'
import { SliderField } from '../BaseField/SliderField'
import TextField from '../BaseField/TextField'
import StyleContent from '../StyleContent'
import type { FieldProps } from '../types'

interface MobileFieldProps extends FieldProps {}

const MobileField: React.FunctionComponent<MobileFieldProps> = props => {
    const {
        value: propValue,
        config,
        onChange,
        onBlur,
        onFocus,
        onKeyDown,
        onAddClickEvent,
        dataSource,
        isValid = false,
        mode = 'normal',
        error
    } = props
    const { canEdit, inputType, fieldPointer = '', required } = config

    const field = dataSource?.schema?.[fieldPointer]

    const isControlled = useMemo(() => propValue !== undefined, [propValue])
    const value = propValue

    const handleChange = useCallback(
        (fieldValue: FieldInputADTValue) => {
            const res = onChange?.(fieldValue)
            return res || false
        },
        [onChange]
    )

    const handleBlur = useCallback(
        (fieldValue: FieldInputADTValue) => {
            const res = onBlur?.(fieldValue)
            return res || false
        },
        [onBlur]
    )

    const handleCellChange = useCallback(
        (fieldValue: FieldInputADTValue) => {
            onChange?.(fieldValue)
        },
        [onChange]
    )

    const fieldProps = useMemo(
        () => ({
            isControlled,
            readOnly: mode === 'normal' ? !canEdit : !canEdit || (!fieldPointer && !NO_FIELD_INPUT.includes(inputType))
        }),
        [canEdit, fieldPointer, inputType, isControlled, mode]
    )

    return (
        <StyleContent field={field} inputType={inputType} error={error} isReadOnly={fieldProps.readOnly} config={config}>
            {useMemo(() => {
                const isEmpty = !fieldPointer || !field
                if (isEmpty && !NO_FIELD_INPUT.includes(inputType) && mode === 'datasource') {
                    return <EmptyField type={inputType} config={config} />
                }
                switch (inputType) {
                    case 'text': {
                        return (
                            <TextField
                                {...props}
                                {...fieldProps}
                                isMobile
                                textConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'phoneNumber': {
                        return (
                            <PhoneNumberField
                                {...props}
                                {...fieldProps}
                                phoneNumberConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'url':
                    case 'email': {
                        return (
                            <SimpleTextField
                                {...props}
                                {...fieldProps}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'number': {
                        const numberValue = isFieldNumberValue(value) ? value : ''
                        const finallyValue = numberReg.test(numberValue.toString()) ? numberValue : ''
                        return (
                            <NumberField
                                {...props}
                                {...fieldProps}
                                value={finallyValue}
                                numberField={field?.type === 'number' ? field : undefined}
                                numberConfig={config}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'slider': {
                        const numberValue = isFieldNumberValue(value) ? value : ''
                        const finallyValue = numberReg.test(numberValue.toString()) ? numberValue : ''
                        return (
                            <SliderField
                                {...props}
                                {...fieldProps}
                                value={finallyValue}
                                sliderConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'date': {
                        return (
                            <DateDrawerField
                                {...props}
                                {...fieldProps}
                                value={isFieldNumberValue(value) ? value : ''}
                                dateField={field}
                                dateConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'file': {
                        return (
                            <FileDrawerField
                                {...props}
                                {...fieldProps}
                                fileConfig={config}
                                fileField={field?.type === 'file' || field?.type === 'video' ? field : undefined}
                                value={isFileValue(value) ? value : []}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'notes': {
                        return (
                            <NotesModalField
                                {...props}
                                {...fieldProps}
                                value={isRichTextValue(value) ? value : undefined}
                                onBlur={handleBlur}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'relativeSelect': {
                        if (field?.type === 'select' || field?.type === 'singleSelect') {
                            return (
                                <SelectDrawerField
                                    {...props}
                                    {...fieldProps}
                                    relativeSelectConfig={config}
                                    selectConfig={field.type === 'select' ? field.select : field.singleSelect}
                                    value={isIdsValue(value) ? value : []}
                                    onChange={handleCellChange}
                                />
                            )
                        }
                        if (field?.type === 'role') {
                            return (
                                <RelativeSelectDrawerField
                                    {...props}
                                    {...fieldProps}
                                    value={value?.toString()}
                                    relativeSelectConfig={mergeDeepRight<RelativeSelectConfig>(config, {
                                        relativeSelect: {
                                            viewFieldSettings: [
                                                {
                                                    type: 'text',
                                                    fieldId: 'ID',
                                                    visible: false,
                                                    width: 160
                                                },
                                                {
                                                    type: 'text',
                                                    fieldId: 'ROLE_NAME',
                                                    visible: true,
                                                    width: 160
                                                }
                                            ]
                                        }
                                    })}
                                    onChange={v =>
                                        handleCellChange({
                                            ...v,
                                            value: isStringArray(v.value) ? v.value : typeof v.value === 'string' ? v.value.split(',') : []
                                        } as FieldInputADTValue)
                                    }
                                    onAddClickEvent={onAddClickEvent}
                                />
                            )
                        }
                        return (
                            <RelativeSelectDrawerField
                                {...props}
                                {...fieldProps}
                                value={isNumberValue(value) ? value.toString() : undefined}
                                relativeSelectConfig={config}
                                onChange={handleCellChange}
                                onAddClickEvent={onAddClickEvent}
                            />
                        )
                    }
                    case 'person': {
                        return (
                            <PersonDrawerField
                                {...props}
                                {...fieldProps}
                                value={isIdsValue(value) ? value : []}
                                personField={field?.type === 'person' ? field : undefined}
                                personConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'checkbox': {
                        return (
                            <CheckboxField
                                {...props}
                                {...fieldProps}
                                value={Boolean(value)}
                                checkBoxConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'cascade': {
                        if (field?.type === 'department' || field?.type === 'parentDepartment') {
                            return (
                                <DepartmentDrawerField
                                    {...props}
                                    {...fieldProps}
                                    cascadeConfig={config}
                                    value={isStringArray(value) ? value : []}
                                    onChange={handleCellChange}
                                />
                            )
                        }

                        return (
                            <CascadeDrawerField
                                {...props}
                                {...fieldProps}
                                cascadeConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'calendar': {
                        return (
                            <CalendarField
                                {...props}
                                {...fieldProps}
                                calendarField={field}
                                calendarConfig={config}
                                value={isFieldNumberValue(value) ? value : ''}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    default: {
                        return null
                    }
                }
            }, [
                fieldPointer,
                field,
                inputType,
                mode,
                config,
                props,
                fieldProps,
                value,
                handleBlur,
                handleChange,
                handleCellChange,
                onAddClickEvent
            ])}
        </StyleContent>
    )
}

export default MobileField
